import * as THREE from 'three';
import { segmentClear } from './collision.js';

/**
 * The third person rig: an orbit around the player's shoulders, driven by the
 * mouse, that pulls in rather than clipping through walls.
 *
 * Bully's camera, not a shooter's. The mouse turns the *camera*, and the
 * character turns to face wherever they are being walked — which is why the
 * body owns its own yaw and this module never sets it. The payoff is that you
 * can run down the service corridor while looking sideways into the break room,
 * which is most of what a stealth-adjacent game asks a camera for.
 *
 * Wall avoidance is a march, not a sphere cast: sample the line from the pivot
 * out to where the camera wants to be, and stop at the last clear sample. A
 * sphere cast would be smoother and would also need a sphere-vs-AABB sweep for
 * a building made entirely of flat walls, where marching lands within a few
 * centimetres of the same answer. The pull-in is eased on the way *out* only —
 * snapping in instantly when a wall arrives is correct, easing in is nausea.
 */

export const RIG = Object.freeze({
  /** The boom indoors, where rooms are small and walls are close. */
  distance: 3.4,
  /** And outdoors, where there is room to see the lot, the lawn and the road. */
  outdoors: 4.4,
  /** Seconds for the boom to settle when you walk in or out of a door. */
  zoneTime: 0.8,
  /**
   * How long the drawn camera takes to catch up with the body and the stick,
   * in seconds. Short: enough to take the jitter off a step and the edge off
   * a turn, never enough to feel late. Steering always reads the true yaw, so
   * this changes what is drawn and nothing about where anybody walks.
   */
  follow: 0.07,
  turn: 0.05,
  /** Metres above the feet the camera orbits around. Chest, not eyes. */
  pivotHeight: 1.45,
  /** Over-the-shoulder offset, so the player is not dead centre. */
  shoulder: 0.45,
  minPitch: -0.55,
  maxPitch: 1.15,
  /** Metres of clearance kept between the camera and anything solid. */
  margin: 0.28,
  /** Seconds for the boom to ease back out once the wall is gone. */
  extendTime: 0.25,
  /** Never closer than this, even jammed in a corner. */
  minimum: 0.9,
  /** Inside this, the body is hidden — the alternative is a screen of shoulder. */
  hideBody: 1.5,
});

/**
 * The camera yaw that puts the camera behind a body facing `yaw`.
 *
 * There are two yaw conventions in this game and both are correct for what they
 * describe. A **body** — the player, every NPC — is drawn as an object rotated
 * by `rotation.y = yaw` with its nose built at local +Z, so a body facing `yaw`
 * points along `(sin yaw, cos yaw)`, and `character.js` computes it that way
 * from the direction of travel. The **camera** is not an object with a facing;
 * it is a position on a boom, and `yaw` here is the direction from the player
 * *out to the camera* — the opposite end of the same line.
 *
 * Which means the one thing you must never do is assign one to the other, and
 * that is what was being done at spawn, on reset and on every restore:
 * `rig.yaw = player.yaw` put the camera exactly where the player was looking.
 * You woke up nose to nose with your own character, and pressing forward walked
 * them away from the camera — which is what "forward moves backward" is.
 *
 * So the conversion has a name and lives next to the convention it converts
 * between, rather than being a `+ Math.PI` sitting in four places in `main.js`
 * that the next person has to work out from first principles.
 */
export const behindBody = (yaw) => yaw + Math.PI;

export function createCameraRig({ world, fov = 62 } = {}) {
  const camera = new THREE.PerspectiveCamera(
    fov,
    window.innerWidth / window.innerHeight,
    0.05,
    400
  );

  let yaw = 0;
  let pitch = 0.20;
  let boom = RIG.distance;
  /** The boom it is heading for, indoors or out. */
  let reachFor = RIG.distance;
  /** What is drawn: the yaw, pitch and pivot, eased toward the true ones. */
  let drawnYaw = null;
  let drawnPitch = null;
  const drawnAt = new THREE.Vector3();
  let fresh = true;

  const pivot = new THREE.Vector3();
  const desired = new THREE.Vector3();
  const offset = new THREE.Vector3();

  function update(dt, target, look = { x: 0, y: 0 }, { outdoors = false } = {}) {
    yaw -= look.x;
    pitch = clamp(pitch + look.y, RIG.minPitch, RIG.maxPitch);

    // What is drawn is eased toward the truth: snapped on the first frame and
    // after a jump (a teleport, a script, a reload), where easing is a swoop.
    const jumped = fresh || drawnYaw === null || Math.hypot(target.x - drawnAt.x, target.z - drawnAt.z) > 2;
    if (jumped) {
      drawnAt.set(target.x, target.y, target.z);
      drawnYaw = yaw;
      drawnPitch = pitch;
    } else {
      const f = 1 - Math.exp(-dt / RIG.follow);
      const t = 1 - Math.exp(-dt / RIG.turn);
      drawnAt.x += (target.x - drawnAt.x) * f;
      drawnAt.y += (target.y - drawnAt.y) * f;
      drawnAt.z += (target.z - drawnAt.z) * f;
      drawnYaw += wrap(yaw - drawnYaw) * t;
      drawnPitch += (pitch - drawnPitch) * t;
    }
    fresh = false;

    // Closer indoors, wider out, eased across the door.
    const wanted = outdoors ? RIG.outdoors : RIG.distance;
    reachFor = jumped ? wanted : approach(reachFor, wanted, (Math.abs(RIG.outdoors - RIG.distance) / RIG.zoneTime) * dt);

    pivot.set(drawnAt.x, drawnAt.y + RIG.pivotHeight, drawnAt.z);
    // The shoulder offset rides the yaw, so it stays on the same side of the
    // player no matter which way the camera has been swung.
    pivot.x += Math.cos(drawnYaw) * RIG.shoulder;
    pivot.z -= Math.sin(drawnYaw) * RIG.shoulder;

    // The direction from the pivot *to* the camera: behind the player and, at
    // a positive pitch, above them. Positive pitch therefore reads as "looking
    // down", which is what pushing the mouse away from you should do.
    offset.set(
      Math.sin(drawnYaw) * Math.cos(drawnPitch),
      Math.sin(drawnPitch),
      Math.cos(drawnYaw) * Math.cos(drawnPitch)
    );

    const reach = world ? clearDistance(pivot, offset, reachFor, world) : reachFor;
    // In immediately, out on a timer.
    boom = reach < boom ? reach : approach(boom, reach, (reachFor / RIG.extendTime) * dt);

    desired.copy(pivot).addScaledVector(offset, boom);
    camera.position.copy(desired);
    camera.lookAt(pivot);
    return camera;
  }

  return {
    camera,
    update,
    get yaw() {
      return yaw;
    },
    set yaw(value) {
      yaw = value;
      // Told where to point, it points there: no swing on the next frame.
      drawnYaw = value;
    },
    get pitch() {
      return pitch;
    },
    /** How far the boom actually got, after walls. */
    get boom() {
      return boom;
    },
    /**
     * Whether the body should be drawn. A boom squeezed against a wall puts the
     * camera inside the player, and a screenful of shoulder is worse than no
     * shoulder at all.
     */
    get bodyVisible() {
      return boom > RIG.hideBody;
    },
    /**
     * Where the camera thinks "forward" is on the floor plane.
     *
     * The camera sits at `pivot + offset * boom` and looks back at the pivot,
     * so its view direction is `-offset`, whose floor part is this. Checked
     * against `camera.getWorldDirection()` rather than against this comment:
     * the dot product is 1.
     */
    get forward() {
      return { x: -Math.sin(yaw), z: -Math.cos(yaw) };
    },
    /**
     * And the right of the screen, which was the left of it.
     *
     * For a right-handed Y-up camera, screen right is `forward × up`. With
     * `forward = (-sin, 0, -cos)` that is `(cos, 0, -sin)`, and this getter
     * returned the exact negation of it — so A and D were swapped, and only A
     * and D: there is one reader, `readIntent`, and it uses this for the strafe
     * term and `forward` for the walk term.
     */
    get right() {
      return { x: Math.cos(yaw), z: -Math.sin(yaw) };
    },
  };
}

function clearDistance(pivot, offset, max, world) {
  const steps = 10;
  let best = max;
  for (let i = steps; i >= 1; i -= 1) {
    const distance = (max * i) / steps;
    const point = {
      x: pivot.x + offset.x * distance,
      y: pivot.y + offset.y * distance,
      z: pivot.z + offset.z * distance,
    };
    if (segmentClear(pivot, point, world)) return Math.max(distance - RIG.margin, RIG.minimum);
    best = distance;
  }
  return Math.max(best - RIG.margin, RIG.minimum);
}

function wrap(angle) {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function approach(value, target, step) {
  if (value < target) return Math.min(value + step, target);
  return Math.max(value - step, target);
}
